import { useEffect, useState, lazy, Suspense } from 'react';
import FrontImage from "../Assets/Images/Front_Image.png";
import "./Home.css";
import Typewriter from "typewriter-effect";

// Lazy load components for better performance
const GoogleReviewSection = lazy(() => import("./GoogleReviewSection.js"));
const ServiceCard = lazy(() => import("./ServiceCard.js"));
const Section4 = lazy(() => import("./Section4.js"));
const LogoSection = lazy(() => import("./LogoSection.js"));
const HomeFAQs = lazy(() => import("./HomeFAQs.js"));
const WhatWeDoSection = lazy(() => import("./WhatWeDoSection.js"));
const ContactForm = lazy(() => import("../ContactForm/ContactFormModal.js"));

const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    const createdMeta = [];

    const setMetaTag = ({ attribute, value, content }) => {
      const selector = `meta[${attribute}="${value}"]`;
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
        createdMeta.push(meta);
      }
      meta.setAttribute('content', content);
    };

    const upsertCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      return link;
    };

    const createJsonLd = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      return script;
    };

    document.title = 'BooksComply | U.S. Bookkeeping, Tax & Company Formation Services in USA';

    setMetaTag({
      attribute: 'name',
      value: 'description',
      content: 'Outsource bookkeeping, tax filing, and company formation for U.S. businesses. Affordable, compliant, and powered by expert accountants in the USA.'
    });
    setMetaTag({
      attribute: 'name',
      value: 'keywords',
      content: 'US bookkeeping outsourcing, US tax filing partner, virtual CFO USA, USA company incorporation support, BooksComply accountants'
    });
    setMetaTag({ attribute: 'name', value: 'robots', content: 'index, follow' });
    setMetaTag({ attribute: 'property', value: 'og:title', content: 'BooksComply | U.S. Bookkeeping, Tax & Company Formation Services in USA' });
    setMetaTag({ attribute: 'property', value: 'og:description', content: 'Outsource bookkeeping, tax filing, and company formation for U.S. businesses. Affordable, compliant, and powered by expert accountants in the USA.' });
    setMetaTag({ attribute: 'property', value: 'og:image', content: 'https://www.bookscomply.com/Assets/Images/Front_Image.png' });
    setMetaTag({ attribute: 'property', value: 'og:url', content: 'https://www.bookscomply.com/' });
    setMetaTag({ attribute: 'name', value: 'twitter:card', content: 'summary_large_image' });

    const canonicalLink = upsertCanonical('https://www.bookscomply.com/');

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'BooksComply',
      url: 'https://www.bookscomply.com',
      logo: 'https://www.bookscomply.com/Assets/Images/BooksCompl.png',
      description: 'BooksComply provides end-to-end US accounting, bookkeeping, tax filing, and incorporation solutions for founders worldwide.',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91 9428482371',
        contactType: 'customer service',
        areaServed: 'USA'
      },
      sameAs: [
        'https://in.linkedin.com/company/bookscomply',
        'https://x.com/njp_advisors'
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '124'
      }
    };

    const servicesSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'BooksComply USA Expansion Services',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/tax-return',
            name: 'Tax Return Services USA',
            description: 'Forget the tax season headaches. Our team will prepare and file your federal and state returns and guide you on every required form.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Tax Preparation',
            serviceOutput: 'Filed US federal and state tax returns with compliance support',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/tax-return',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/bookkeeping',
            name: 'U.S. Bookkeeping Services',
            description: 'Stay prepared with dependable bookkeeping services USA. Outsource your bookkeeping to our team, and enjoy the good financial reports and reliable compliance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Bookkeeping Service',
            serviceOutput: 'GAAP-compliant monthly financial statements',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/bookkeeping',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/cfo-advisory',
            name: 'Virtual CFO Services USA for Startups',
            description: 'Grow the same with confidence. With our virtual CFO services for startups USA, you’ll gain niche financial insights and better decision-making—without hiring a CFO.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Financial Advisory Service',
            serviceOutput: 'Strategic finance models and investor-ready dashboards',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/cfo-advisory',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Service',
            '@id': 'https://bookscomply.com/usa-company-formation',
            name: 'USA Company Formation Services',
            description: 'Build the U.S. business incorporation simple. We guide you through state selection, document preparation, EIN registration, and IRS compliance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Business Incorporation Service',
            serviceOutput: 'Formed US legal entity with compliance roadmap',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/usa-company-formation',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        }
      ]
    };

    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: 'Chirag Darji'
      },
      itemReviewed: {
        '@type': 'Organization',
        name: 'BooksComply'
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5'
      },
      reviewBody: 'BooksComply handled our Wyoming incorporation, filings, and ongoing compliance with speed and transparency.'
    };

    const organizationScript = createJsonLd('home-organization-schema', organizationSchema);
    const servicesScript = createJsonLd('home-services-schema', servicesSchema);
    const reviewScript = createJsonLd('home-review-schema', reviewSchema);

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How to start a company in the USA?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "BooksComply guides you through every state’s requirements—step-by-step. Stay informed at each stage with regular updates."
          }
        },
        {
          '@type': 'Question',
          name: 'How long does U.S. company formation take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most businesses are set up in just a few days. Non-resident founders may experience a longer wait for EIN registration.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I outsource U.S. tax filing if I live abroad?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes—you can! BooksComply supports expat entrepreneurs worldwide, managing federal and state filings remotely.'
          }
        },
        {
          '@type': 'Question',
          name: 'What are the most affordable bookkeeping services in the USA for startups?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Many startups look for bookkeeping that is budget-friendly yet reliable. Services like BooksComply specialize in affordable bookkeeping solutions tailored for small businesses and startups'
          }
        }
      ]
    };
    const faqScript = createJsonLd('home-faq-schema', faqSchema);

    return () => {
      createdMeta.forEach((meta) => document.head.removeChild(meta));
      [organizationScript, servicesScript, reviewScript, faqScript].forEach((script) => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      if (canonicalLink && canonicalLink.parentNode) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
    };
  }, []);

  const handleCloseContactForm = () => {
    setShowContactForm(false);
  };

  return (
    <main className="home-page">
      <section id="homeSection" className="bg-light pt-5 pb-3">
        <div className="container">
          <div className="row align-items-center">
            {/* Left Column: Taglines */}
            <div className="col-md-6">
              <h1 className="display-3 fw-bolder mb-3">
                <span className="text-black-force">Grow Your U.S. Business with Trusted</span>
                <br />
                <span className="typewriter-text text-accent fw-normal font-change font-change1">
                  <Typewriter
                    options={{
                      strings: ["Bookkeeping", "CFO Advisory", "USA Company Formation", "Tax Return"],
                      autoStart: true,
                      loop: true,
                      deleteSpeed: 0.1,
                    }}
                  />
                </span>
              </h1>
              <p className="lead mt-4 text-black-force" style={{ color: '#000000' }}>
                Starting and growing a business in the United States brings new opportunities—but as your business expands, 
                managing tax filing, bookkeeping, and compliance may start to slow you down. BooksComply helps founders, 
                startups, and small companies keep things simple. From U.S. company formation to daily accounting, 
                we make every step easier so you stay focused on success.
              </p>
              <div className="d-flex flex-wrap gap-3 mt-4">
                <button
                  className="btn btn-primary-1 rounded-pill px-4 py-2"
                  onClick={() => setShowContactForm(true)}
                  style={{
                    backgroundColor: '#0d6efd',
                    border: 'none',
                    boxShadow: '0 4px 6px rgba(13, 110, 253, 0.2)',
                    transition: 'all 0.3s ease',
                    minWidth: '200px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(13, 110, 253, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(13, 110, 253, 0.2)';
                  }}
                >
                  Book a Free Consultation
                </button>
                <button
                  className="btn btn-outline-primary rounded-pill px-4 py-2"
                  onClick={() => window.location.href = '/pricing'}
                  style={{
                    color: '#0d6efd',
                    borderColor: '#0d6efd',
                    backgroundColor: 'transparent',
                    transition: 'all 0.3s ease',
                    minWidth: '150px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  View Pricing
                </button>
              </div>
            </div>

            {/* Right Column: Image */}
            <div className="col-md-6 text-center">
              <div className="front-image-wrap">
                <img
                  src={FrontImage}
                  alt="Bookkeeping Services"
                  className="front-image"
                  loading="eager"
                  fetchpriority="high"
                  width="600"
                  height="520"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}>
        <ServiceCard />
        <WhatWeDoSection />
        <GoogleReviewSection />
        <Section4 />
        <LogoSection />
        <HomeFAQs />
      </Suspense>

      {/* Contact Form Modal */}
      {showContactForm && (
        <Suspense fallback={<div>Loading...</div>}>
          <div className="modal-overlay">
            <div className="modal-content">
              <ContactForm onClose={handleCloseContactForm} />
            </div>
          </div>
        </Suspense>
      )}
    </main>
  );
};
export default Home;